import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, FileUp, Loader2, CheckCircle2, Trophy, XCircle, Trash2, Plus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Phase 40.28 — Upload a PDF answer paper → GPT-4o OCRs it into an editable
 * draft (student name, class, board, subject, year, and every Q/A pair) →
 * faculty confirms → AI grades → shows the final result card.
 */
export default function PaperScanPage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [phase, setPhase] = useState('upload'); // 'upload' | 'draft' | 'result'
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [result, setResult] = useState(null);

  const authHeader = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const uploadPdf = async (f) => {
    if (!f) return;
    if (!/\.pdf$/i.test(f.name)) return toast.error('Please upload a PDF file.');
    if (f.size > 15 * 1024 * 1024) return toast.error('PDF must be under 15 MB.');
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', f);
      const res = await fetch(`${API}/api/ocr-paper/upload`, {
        method: 'POST', headers: authHeader(), credentials: 'include', body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      setDraft(await res.json());
      setPhase('draft');
      toast.success('Paper scanned — please review the draft below.');
    } catch (err) {
      toast.error(err.message || 'Failed to scan paper');
    } finally {
      setBusy(false);
    }
  };

  const updateAns = (i, patch) => setDraft(d => ({
    ...d, answers: d.answers.map((a, idx) => idx === i ? { ...a, ...patch } : a),
  }));
  const removeAns = (i) => setDraft(d => ({ ...d, answers: d.answers.filter((_, idx) => idx !== i) }));
  const addAns = () => setDraft(d => ({
    ...d,
    answers: [...(d.answers || []), { question_number: `Q${(d.answers?.length || 0) + 1}`, question: '', student_answer: '', marks: 5 }],
  }));

  const confirmAndGrade = async () => {
    if (!draft?.student_name?.trim()) return toast.error("Student name can't be empty");
    if (!draft?.answers?.length) return toast.error('At least one Q/A is required');
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/ocr-paper/${draft.draft_id}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        credentials: 'include',
        body: JSON.stringify({
          student_name: draft.student_name,
          class_level: draft.class_level,
          board: draft.board,
          subject: draft.subject,
          year: draft.year,
          answers: draft.answers.map(a => ({
            question: a.question,
            student_answer: a.student_answer,
            marks: Number(a.marks) || 5,
            expected_answer: a.expected_answer || null,
          })),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      setResult(await res.json());
      setPhase('result');
      toast.success('Paper graded!');
    } catch (err) {
      toast.error(err.message || 'Grading failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="paper-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-semibold">Handwritten Answer Paper — AI Grader</h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* PHASE 1 — Upload */}
        {phase === 'upload' && (
          <div className="p-8 rounded-2xl border-2 border-dashed border-primary/40 bg-primary/5 text-center space-y-4">
            <FileUp className="w-12 h-12 mx-auto text-primary" />
            <h2 className="text-xl font-semibold">Upload the scanned answer paper (PDF)</h2>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              We'll extract the student's name, class, board, subject, year, and every question &amp; answer.
              You'll get a chance to edit the draft before grading.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => uploadPdf(e.target.files?.[0])}
              data-testid="paper-file-input"
            />
            <Button
              size="lg"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
              className="bg-primary hover:bg-primary/90"
              data-testid="paper-upload-btn"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scanning…</> : <><FileUp className="w-4 h-4 mr-2" />Choose PDF</>}
            </Button>
            <p className="text-[11px] text-muted-foreground">Max 15 MB · up to 10 pages · scans best at 150+ DPI</p>
          </div>
        )}

        {/* PHASE 2 — Editable Draft */}
        {phase === 'draft' && draft && (
          <div className="space-y-6" data-testid="paper-draft">
            <div className="p-4 rounded-xl border bg-amber-50 dark:bg-amber-950/20 border-amber-300 text-sm">
              <strong>Draft — please verify.</strong> The AI read {draft.pages} page(s) and extracted {draft.answers.length} Q/A pair(s).
              Edit any field, then click <em>Confirm & Grade</em> below.
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4 rounded-xl border bg-card/40">
              {[
                ['student_name', 'Student Name', true],
                ['class_level', 'Class / Grade'],
                ['board', 'Board (CBSE / ICSE / SSC …)'],
                ['subject', 'Subject'],
                ['year', 'Year (e.g. 2025-26)'],
                ['paper_title', 'Paper Title (optional)'],
              ].map(([key, label, req]) => (
                <div key={key} className="space-y-1">
                  <Label className="text-xs">{label}{req && <span className="text-red-500"> *</span>}</Label>
                  <Input
                    value={draft[key] || ''}
                    onChange={(e) => setDraft(d => ({ ...d, [key]: e.target.value }))}
                    placeholder={label}
                    data-testid={`paper-meta-${key}`}
                  />
                </div>
              ))}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">Questions &amp; Answers ({draft.answers.length})</h3>
                <Button variant="outline" size="sm" onClick={addAns} data-testid="paper-add-qa"><Plus className="w-3 h-3 mr-1" />Add Q/A</Button>
              </div>
              {draft.answers.map((a, i) => (
                <div key={i} className="p-4 rounded-xl border bg-card/40 space-y-2" data-testid={`paper-qa-${i}`}>
                  <div className="flex items-center justify-between gap-3">
                    <Input
                      value={a.question_number || ''}
                      onChange={(e) => updateAns(i, { question_number: e.target.value })}
                      className="w-24"
                    />
                    <Input
                      type="number"
                      min={1}
                      value={a.marks || 5}
                      onChange={(e) => updateAns(i, { marks: e.target.value })}
                      className="w-24"
                      placeholder="Marks"
                    />
                    <Button variant="ghost" size="icon" onClick={() => removeAns(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                  <textarea
                    value={a.question || ''}
                    onChange={(e) => updateAns(i, { question: e.target.value })}
                    placeholder="Question text"
                    className="w-full h-16 p-2 rounded-lg border text-sm bg-background"
                  />
                  <textarea
                    value={a.student_answer || ''}
                    onChange={(e) => updateAns(i, { student_answer: e.target.value })}
                    placeholder="Student's answer (as OCR'd)"
                    className="w-full h-28 p-2 rounded-lg border text-sm bg-background"
                  />
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                size="lg"
                onClick={confirmAndGrade}
                disabled={busy}
                data-testid="paper-confirm-btn"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Grading…</> : <><CheckCircle2 className="w-4 h-4 mr-2" />Confirm &amp; Grade</>}
              </Button>
              <Button variant="ghost" onClick={() => { setPhase('upload'); setDraft(null); }}>Discard &amp; restart</Button>
            </div>
          </div>
        )}

        {/* PHASE 3 — Result */}
        {phase === 'result' && result && (
          <div className="space-y-6" data-testid="paper-result">
            <div className={`p-8 rounded-2xl text-center border-2 ${result.passed ? 'border-emerald-500/40 bg-emerald-50/50 dark:bg-emerald-950/20' : 'border-red-500/40 bg-red-50/50 dark:bg-red-950/20'}`}>
              <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${result.passed ? 'bg-emerald-100 dark:bg-emerald-900/40' : 'bg-red-100 dark:bg-red-900/40'}`}>
                {result.passed ? <Trophy className="w-10 h-10 text-emerald-600" /> : <XCircle className="w-10 h-10 text-red-500" />}
              </div>
              <h2 className="text-3xl font-bold mb-1" data-testid="paper-result-pct">{result.percentage}%</h2>
              <p className="text-muted-foreground mb-1">{result.total_score} / {result.max_score}</p>
              <p className="text-xs text-muted-foreground mb-3">{result.student_name} · {result.subject} · {result.board} {result.class_level}</p>
              <span className={`inline-block px-4 py-1 rounded-full text-sm font-semibold ${result.passed ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{result.passed ? 'PASSED' : 'NEEDS IMPROVEMENT'}</span>
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold text-lg">Question-by-question review</h3>
              {result.results.map((r, i) => (
                <div key={i} className={`p-4 rounded-xl border ${r.marks_awarded === r.max_marks ? 'border-emerald-200 bg-emerald-50/30 dark:border-emerald-800 dark:bg-emerald-950/20' : r.marks_awarded === 0 ? 'border-red-200 bg-red-50/30 dark:border-red-800 dark:bg-red-950/20' : 'border-amber-200 bg-amber-50/30 dark:border-amber-800 dark:bg-amber-950/20'}`}>
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-sm font-medium flex-1">Q{i + 1}. {r.question}</p>
                    <span className="text-sm font-bold text-primary shrink-0 ml-2">{r.marks_awarded}/{r.max_marks}</span>
                  </div>
                  <p className="text-xs text-muted-foreground italic mb-1">Your answer: {r.student_answer.slice(0, 240)}{r.student_answer.length > 240 ? '…' : ''}</p>
                  <p className="text-xs"><strong>Feedback:</strong> {r.feedback}</p>
                  {r.improvements && <p className="text-xs mt-1 text-amber-700 dark:text-amber-400"><strong>Improve:</strong> {r.improvements}</p>}
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <Button onClick={() => { setPhase('upload'); setDraft(null); setResult(null); }} data-testid="paper-scan-another">Scan another paper</Button>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
